import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate, useSelectedRoutes } from 'umi';
import '@/pages/ISWAN_APP/layout/H5CommonLayout/H5Navbar/style.less';
interface H5NavbarProps {
  children?: React.ReactNode;
  showBack?: boolean;
  showForward?: boolean;
  customTitle?: string;
}

// WebView 环境检测工具函数
const isWebViewEnv = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /(micromessenger|alipay|myapp|webview|wv)/i.test(userAgent);
};

export const H5Navbar: React.FC<H5NavbarProps> = (props) => {
  const { children, showBack = true, showForward = true, customTitle } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const selectedRoutes = useSelectedRoutes();
  // 状态管理
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [canGoForward, setCanGoForward] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  // 使用 ref 来持久化状态，避免重新渲染时丢失
  const isInWebView = useRef(isWebViewEnv());
  const routeHistoryRef = useRef<string[]>([]);
  const currentIndexRef = useRef(0);
  // WebView 环境下的路由历史管理
  useEffect(() => {
    const currentPath = location.pathname + location.search;

    if (isInWebView.current) {
      console.log('WebView路由处理:', {
        currentPath,
        previousHistory: routeHistoryRef.current,
        currentIndex: currentIndexRef.current
      });

      // 初始化历史记录
      if (routeHistoryRef.current.length === 0) {
        routeHistoryRef.current = [currentPath];
        currentIndexRef.current = 0;
        setCanGoBack(false);
        setCanGoForward(false);
        console.log('初始化WebView路由历史:', routeHistoryRef.current);
        return;
      }

      const history = routeHistoryRef.current;
      const currentIndex = currentIndexRef.current;
      const currentRoute = history[currentIndex];

      // 判断导航类型
      if (currentPath === currentRoute) {
        // 相同路径，不处理（可能是刷新）
        return;
      } else if (currentIndex > 0 && currentPath === history[currentIndex - 1]) {
        // 后退操作
        console.log('检测到后退操作');
        currentIndexRef.current = currentIndex - 1;
      } else if (currentIndex < history.length - 1 && currentPath === history[currentIndex + 1]) {
        // 前进操作
        console.log('检测到前进操作');
        currentIndexRef.current = currentIndex + 1;
      } else {
        // 新路由导航：清除当前索引之后的历史，添加新路由
        console.log('检测到新路由导航');
        const newHistory = history.slice(0, currentIndex + 1);
        newHistory.push(currentPath);
        routeHistoryRef.current = newHistory;
        currentIndexRef.current = newHistory.length - 1;
      }

      // 更新导航按钮状态
      const newIndex = currentIndexRef.current;
      const newHistory = routeHistoryRef.current;

      // 返回按钮：只要不在历史的第一项就显示
      const shouldShowBack = newIndex > 0;
      // 前进按钮：当前不是历史的最后一项
      const shouldShowForward = newIndex < newHistory.length - 1;

      setCanGoBack(shouldShowBack);
      setCanGoForward(shouldShowForward);

      console.log('更新WebView导航状态:', {
        history: newHistory,
        currentIndex: newIndex,
        canGoBack: shouldShowBack,
        canGoForward: shouldShowForward
      });

    } else {
      // 浏览器环境：使用标准 History API
      const handlePopState = () => {
        setCanGoForward(window.history.state?.idx < window.history.length - 1);
        setCanGoBack(window.history.length > 1);
      };

      window.addEventListener('popstate', handlePopState);
      handlePopState(); // 初始化调用
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [location]);
  // 页面可见性处理 - 解决WebView中页面恢复时的状态问题
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isInWebView.current) {
        // 页面重新可见时，重新计算导航状态
        const shouldShowBack = currentIndexRef.current > 0;
        const shouldShowForward = currentIndexRef.current < routeHistoryRef.current.length - 1;
        console.log('页面重新可见，更新导航状态:', {
          currentIndex: currentIndexRef.current,
          historyLength: routeHistoryRef.current.length,
          canGoBack: shouldShowBack,
          canGoForward: shouldShowForward
        });
        setCanGoBack(shouldShowBack);
        setCanGoForward(shouldShowForward);
        setRefreshTrigger(prev => prev + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 获取路由标题
  const routeTitle = useMemo(() => {
    if (customTitle) return customTitle;
    const matchedRoute = selectedRoutes[selectedRoutes.length - 1]?.route;
    return matchedRoute?.name || '';
  }, [customTitle, selectedRoutes]);

  // 处理导航动作
  const handleBack = () => {
    if (canGoBack) {
      console.log('执行返回操作:', {
        currentIndex: currentIndexRef.current,
        history: routeHistoryRef.current
      });
      if (isInWebView.current) {
        // WebView：先更新索引状态
        currentIndexRef.current -= 1;
        const newIndex = currentIndexRef.current;
        const history = routeHistoryRef.current;
        setCanGoBack(newIndex > 0);
        setCanGoForward(newIndex < history.length - 1);
      }
      navigate(-1);
      // 添加延迟强制重渲染，解决WebView返回白屏问题
      setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('resize'));
        }
      }, 100);
    }
  };

  // 处理前进动作
  const handleForward = () => {
    if (canGoForward) {
      console.log('执行前进操作:', {
        currentIndex: currentIndexRef.current,
        history: routeHistoryRef.current
      });

      if (isInWebView.current) {
        // WebView：先更新索引状态
        currentIndexRef.current += 1;
        const newIndex = currentIndexRef.current;
        const history = routeHistoryRef.current;

        setCanGoBack(newIndex > 0);
        setCanGoForward(newIndex < history.length - 1);
      }

      navigate(1);

      setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
      }, 100);
    }
  };

  // 调试信息
  useEffect(() => {
    console.log('导航组件状态:', {
      environment: isInWebView.current ? 'WebView' : 'Browser',
      currentPath: location.pathname + location.search,
      canGoBack,
      canGoForward,
      historyLength: routeHistoryRef.current.length,
      currentIndex: currentIndexRef.current,
      refreshTrigger
    });
  }, [location, canGoBack, canGoForward, refreshTrigger]);

  return (
    <div className="h5-container" key={refreshTrigger}>
      {/* 导航栏 - 精确边距控制 */}
      <div className="h5-navbar">
        {/* 左侧箭头（严格左对齐） */}
        {(showBack && canGoBack) && <div className={`nav-arrow left`} onClick={handleBack}></div>}

        {/* 绝对居中的标题 */}
        <div className="nav-title">{routeTitle}</div>

        {/* 右侧箭头（严格右对齐） */}
        {(showForward && canGoForward) && (
          <div className="nav-arrow right" onClick={handleForward}></div>
        )}
      </div>

      {/* 页面内容 - 添加样式类 */}
      <div className="h5-content">{children}</div>
    </div>
  );
};
